import i18n from '@/i18n/i18n'
import { AuthService } from '@/services/authService'
import { POSService } from '@/services/posService'
import { Auth } from '@/types/common/httpRequest'
import { CashierShortTokenDecoded } from '@/types/pos/cashier'

/**
 * Payload for /api/v1/printer/print-slip. The device prints the raw text
 * fields exactly as passed (no formatting / conversion). Optional fields that
 * are omitted simply do not appear on the slip.
 */
export interface PrintSlipRequest {
    companyName?: string
    companyAddress?: string
    companyCity?: string
    terminalId?: string
    merchantId?: string
    locationName?: string
    locationAddress?: string
    transactionType?: string
    cardIssuer?: string
    cardNumber?: string
    cardApplicationName?: string
    invoiceNumber?: string
    approvalCode?: string
    rrn?: string
    responseCode?: string
    amount?: string
    currency?: string
    transactionStatus?: string
    transactionDate?: string
    transactionTime?: string
    copyType?: string
    supportPhone?: string
}

export interface PosSlipActivationRequest {
    amount: string
    currency: string
    terminalId: string
}

export interface PosSlipActivationResponse {
    success: boolean
    data: Record<string, unknown> | null | undefined
    timestamp: string
}

type PosSlipContext = {
    merchantId?: string
    locationName?: string
    locationAddress?: string
}

let cachedPosSlipContext: PosSlipContext | null = null
let posSlipContextPromise: Promise<PosSlipContext> | null = null

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

const decodeJwtPayload = <T>(token: string): T | null => {
    try {
        const payloadSegment = token.split('.')[1]
        if (!payloadSegment) {
            return null
        }

        const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/')
        const decoded = atob(normalized)
        return JSON.parse(decoded) as T
    } catch {
        return null
    }
}

const getCashierTokenPayload = (): CashierShortTokenDecoded | null => {
    const rawToken = AuthService.GetToken(Auth.Cashier)
    if (!rawToken) {
        return null
    }

    const token = rawToken.startsWith('Bearer ')
        ? rawToken.substring(7)
        : rawToken

    return decodeJwtPayload<CashierShortTokenDecoded>(token)
}

const getPosSlipStaticFields = (): Pick<
    PrintSlipRequest,
    | 'companyName'
    | 'companyAddress'
    | 'companyCity'
    | 'copyType'
    | 'supportPhone'
> => ({
    companyName: i18n.t('posSlip.companyName'),
    companyAddress: i18n.t('posSlip.companyAddress'),
    companyCity: i18n.t('posSlip.companyCity'),
    copyType: i18n.t('posSlip.copyType'),
    supportPhone: i18n.t('posSlip.supportPhone')
})

const loadPosSlipContext = async (): Promise<PosSlipContext> => {
    if (cachedPosSlipContext) {
        return cachedPosSlipContext
    }

    if (posSlipContextPromise) {
        return posSlipContextPromise
    }

    posSlipContextPromise = (async () => {
        const context: PosSlipContext = {}

        const tokenPayload = getCashierTokenPayload()
        if (tokenPayload?.agent_user_id !== undefined) {
            context.merchantId = String(tokenPayload.agent_user_id)
        }

        try {
            const posInfo = await POSService.getCashiersPOS()
            if (posInfo.venue?.name) {
                context.locationName = posInfo.venue.name
            }
            if (posInfo.venue?.address) {
                context.locationAddress = posInfo.venue.address
            }
        } catch (error) {
            console.warn('POS slip: failed to load venue info from token API', error)
        }

        cachedPosSlipContext = context
        return context
    })()

    try {
        return await posSlipContextPromise
    } finally {
        posSlipContextPromise = null
    }
}

const parseSlipAmount = (value: string | undefined): number | undefined => {
    if (!value) {
        return undefined
    }

    const normalized = value.trim().replace(/\s/g, '')
    if (!normalized) {
        return undefined
    }

    const parsed = Number(normalized.replace(',', '.'))
    return Number.isFinite(parsed) ? parsed : undefined
}

/** Formats amount as a dot-separated string with exactly 2 decimal places. */
const formatSlipAmount = (value: string | undefined): string | undefined => {
    const parsed = parseSlipAmount(value)
    return parsed !== undefined ? parsed.toFixed(2) : undefined
}

const formatFailSlipTimestamp = (
    isoTimestamp: string
): { transactionDate: string; transactionTime: string } => {
    const date = new Date(isoTimestamp)
    const pad = (value: number) => value.toString().padStart(2, '0')

    return {
        transactionDate: `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`,
        transactionTime: `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }
}

/**
 * Normalises the raw TransactionDate field from POS terminal responses to
 * Serbian format (DD.MM.YYYY). POS hardware typically returns a compact
 * digit-only string (DDMMYY or DDMMYYYY). Already-formatted values are
 * returned unchanged.
 */
const formatPosDate = (value: string): string => {
    const digits = value.replace(/\D/g, '')
    if (digits.length === 6) {
        return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.20${digits.slice(4, 6)}`
    }
    if (digits.length === 8) {
        return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`
    }
    return value
}

/**
 * Normalises the raw TransactionTime field from POS terminal responses to
 * HH:MM:SS. POS hardware typically returns a compact digit-only string
 * (HHMMSS). Already-formatted values are returned unchanged.
 */
const formatPosTime = (value: string): string => {
    const digits = value.replace(/\D/g, '')
    if (digits.length === 6) {
        return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}`
    }
    return value
}

export async function buildPosSlipRequest(
    request: PosSlipActivationRequest,
    response: PosSlipActivationResponse
): Promise<PrintSlipRequest> {
    const data: Record<string, unknown> = isRecord(response.data)
        ? response.data
        : {}
    const pick = (key: string): string | undefined => {
        const value = data[key]
        return typeof value === 'string' && value.length > 0 ? value : undefined
    }

    const context = await loadPosSlipContext()
    const failTimestamp = formatFailSlipTimestamp(response.timestamp)
    const amount =
        formatSlipAmount(pick('Amount')) ?? formatSlipAmount(request.amount)

    const rawDate = pick('TransactionDate')
    const rawTime = pick('TransactionTime')

    return {
        ...getPosSlipStaticFields(),
        merchantId: pick('MidNumber') ?? context.merchantId,
        locationName: context.locationName,
        locationAddress: context.locationAddress,
        terminalId: pick('TerminalID'),
        invoiceNumber: pick('InvoiceNumber'),
        transactionDate: rawDate
            ? formatPosDate(rawDate)
            : failTimestamp.transactionDate,
        transactionTime: rawTime
            ? formatPosTime(rawTime)
            : failTimestamp.transactionTime,
        cardIssuer: pick('CardIssuer'),
        cardNumber: pick('CardNumber'),
        cardApplicationName: pick('CardApplicationName'),
        rrn: pick('RRN'),
        responseCode: pick('ResponseCode'),
        amount,
        currency: request.currency === '941' ? 'RSD' : request.currency,
        // Required by print-slip API. Declined activations can return `data:
        // null`, so fall back to printable defaults that mirror the terminal.
        transactionType: pick('TransactionType') ?? '01',
        approvalCode: pick('ApprovalCode') ?? '------',
        // Use response.success as the authoritative source — the terminal's
        // TransactionStatus field can contain non-"Approved" values even on a
        // successful activation (e.g. locale-specific or status codes).
        transactionStatus: response.success
            ? 'Approved'
            : (pick('TransactionStatus') ?? 'Declined')
    }
}
